import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { FloatingHearts } from "./FloatingHearts";
import { PORTAL_CORRIDOR, LOVERS_IMG } from "../data/deck";

const Door = ({ side }) => (
  <motion.div
    className="absolute top-0 bottom-0 z-20 w-1/2 border-[#f7c873]/40"
    style={{
      [side]: 0,
      background:
        "repeating-linear-gradient(180deg, #6a2a3a 0px, #4a1c2a 14px, #5a2333 28px), radial-gradient(circle at 50% 30%, rgba(247,200,115,0.25), transparent 60%)",
      borderRight: side === "left" ? "2px solid rgba(247,200,115,0.6)" : "none",
      borderLeft: side === "right" ? "2px solid rgba(247,200,115,0.6)" : "none",
      boxShadow: "inset 0 0 60px rgba(0,0,0,0.6)",
    }}
    initial={{ x: 0 }}
    animate={{ x: side === "left" ? "-100%" : "100%" }}
    transition={{ duration: 2.2, delay: 0.5, ease: [0.7, 0, 0.3, 1] }}
  >
    <div
      className="absolute inset-y-0 w-[3px]"
      style={{ [side === "left" ? "right" : "left"]: 0, background: "linear-gradient(180deg, transparent, #f7c873, transparent)" }}
    />
  </motion.div>
);

export const PortalTransition = ({ lang, onEnter }) => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setReady(true), 3200);
    return () => clearTimeout(id);
  }, []);

  return (
    <div className="relative min-h-[calc(100vh-4rem)] overflow-hidden heart-nebula flex items-center justify-center">
      {/* Corridor light background */}
      <motion.div
        className="absolute inset-0"
        initial={{ opacity: 0, scale: 1.1 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 2.5, delay: 0.6 }}
      >
        <img src={PORTAL_CORRIDOR} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#2a0f18]/80" />
      </motion.div>

      {/* Central golden glow */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
        style={{ width: 520, height: 520, background: "radial-gradient(circle, rgba(255,225,170,0.55), transparent 65%)" }}
        initial={{ opacity: 0, scale: 0.4 }}
        animate={{ opacity: 1, scale: 1.2 }}
        transition={{ duration: 2.5, delay: 1 }}
      />

      <FloatingHearts count={16} />

      {/* The doors opening */}
      <Door side="left" />
      <Door side="right" />

      {/* The Lovers card emerging */}
      <motion.div
        className="relative z-30 flex flex-col items-center"
        initial={{ opacity: 0, y: 120, scale: 0.6 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 1.8, delay: 1.6, ease: "easeOut" }}
      >
        <div className="relative">
          <motion.div
            className="rounded-2xl overflow-hidden border-2 border-[#f7c873]/70"
            style={{ width: 220, aspectRatio: "0.67", boxShadow: "0 0 60px rgba(255,200,120,0.7)" }}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
          >
            <img src={LOVERS_IMG} alt="Die Liebenden" className="w-full h-full object-cover" />
          </motion.div>
          {[...Array(8)].map((_, i) => (
            <motion.span
              key={i}
              className="absolute"
              style={{
                left: `${10 + (i * 80) / 8 + (i % 2 ? 5 : -5)}%`,
                top: `${(i * 70) / 8}%`,
              }}
              animate={{ opacity: [0, 1, 0], scale: [0.4, 1, 0.4] }}
              transition={{ duration: 2, delay: 1.8 + i * 0.25, repeat: Infinity, repeatDelay: 1 }}
            >
              <Sparkles className="w-4 h-4 text-[#ffe1aa]" />
            </motion.span>
          ))}
        </div>
        <motion.p
          className="font-ornate rose-gold-text glow-rose text-2xl mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 2.4, duration: 1 }}
        >
          {lang === "de" ? "Die Liebenden" : "The Lovers"}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={ready ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mt-8"
        >
          {ready && (
            <Button
              data-testid="portal-enter-btn"
              onClick={onEnter}
              className="font-ornate bg-gradient-to-r from-[#f7c873] via-[#ffb6a0] to-[#ff8fa3] text-[#3d1020] hover:opacity-95 rounded-full px-9 py-6 text-base tracking-wide shadow-[0_0_36px_rgba(255,150,130,0.55)] border border-white/30"
            >
              {lang === "de" ? "In das Licht eintreten" : "Enter the light"}
            </Button>
          )}
        </motion.div>
      </motion.div>
    </div>
  );
};
